import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ReactionRow, useReactions } from '../components/ReactionBar';
import type { ProjectDetail } from './ProjectHome';

type Discussion = { id: number; message: string | null; user_name: string; created_at: string };
type Meeting = {
  id: number;
  title: string;
  content: string | null;
  decisions: string | null;
  meeting_date: string | null;
  created_by_name: string | null;
  created_at: string;
};
type Decision = { id: number; summary: string; context: string | null; validated_by_name: string | null; decided_at: string };

function DiscussionTab({
  messages,
  msgDraft,
  setMsgDraft,
  postMessage,
}: {
  messages: Discussion[];
  msgDraft: string;
  setMsgDraft: (s: string) => void;
  postMessage: (e: FormEvent) => void | Promise<void>;
}) {
  const ids = useMemo(() => messages.map((m) => m.id), [messages]);
  const { reactions, toggle } = useReactions('discussion', ids);
  return (
    <div className="card">
      <form onSubmit={postMessage} style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <input
          placeholder="Nouveau message…"
          value={msgDraft}
          onChange={(e) => setMsgDraft(e.target.value)}
          style={{
            flex: 1,
            padding: 10,
            border: '1px solid var(--c-border)',
            borderRadius: 6,
          }}
        />
        <button className="btn btn-primary">Envoyer</button>
      </form>
      {messages.length === 0 && <div className="card-meta">Aucun message.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m) => (
          <div
            key={m.id}
            style={{
              padding: 12,
              background: 'var(--c-nav)',
              borderRadius: 8,
            }}
          >
            <div className="card-meta">
              <strong>{m.user_name}</strong> · {new Date(m.created_at).toLocaleString('fr-FR')}
            </div>
            <div>{m.message}</div>
            <ReactionRow
              emojis={reactions[m.id] ?? {}}
              onToggle={(emoji) => toggle(m.id, emoji)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProjectCommunication() {
  useOutletContext<ProjectDetail>();
  const { id: projectId } = useParams<{ id: string }>();
  const [tab, setTab] = useState<'discussion' | 'meetings' | 'decisions'>('discussion');

  const [messages, setMessages] = useState<Discussion[]>([]);
  const [msgDraft, setMsgDraft] = useState('');
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);

  const [mTitle, setMTitle] = useState('');
  const [mDate, setMDate] = useState('');
  const [mContent, setMContent] = useState('');
  const [mDecisions, setMDecisions] = useState('');

  const [dSummary, setDSummary] = useState('');
  const [dContext, setDContext] = useState('');

  async function refresh() {
    if (!projectId) return;
    if (tab === 'discussion') {
      const r = await api.get<{ messages: Discussion[] }>(`/projects/${projectId}/discussions`);
      setMessages(r.messages);
    } else if (tab === 'meetings') {
      const r = await api.get<{ meetings: Meeting[] }>(`/projects/${projectId}/meetings`);
      setMeetings(r.meetings);
    } else {
      const r = await api.get<{ decisions: Decision[] }>(`/projects/${projectId}/decisions`);
      setDecisions(r.decisions);
    }
  }
  useEffect(() => {
    refresh();
  }, [projectId, tab]);

  async function postMessage(e: FormEvent) {
    e.preventDefault();
    if (!msgDraft.trim() || !projectId) return;
    await api.post(`/projects/${projectId}/discussions`, { message: msgDraft.trim() });
    setMsgDraft('');
    refresh();
  }

  async function createMeeting(e: FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    await api.post(`/projects/${projectId}/meetings`, {
      title: mTitle,
      content: mContent || null,
      decisions: mDecisions || null,
      meeting_date: mDate || null,
    });
    setMTitle('');
    setMContent('');
    setMDecisions('');
    setMDate('');
    refresh();
  }

  async function createDecision(e: FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    await api.post(`/projects/${projectId}/decisions`, {
      summary: dSummary,
      context: dContext || null,
    });
    setDSummary('');
    setDContext('');
    refresh();
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 6, margin: '8px 0 16px' }}>
        {(['discussion', 'meetings', 'decisions'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`btn btn-ghost ${tab === t ? 'btn-pill-active' : ''}`}
            style={{ padding: '6px 12px', fontSize: 13 }}
          >
            {t === 'discussion' ? 'Discussion' : t === 'meetings' ? 'Réunions' : 'Décisions'}
          </button>
        ))}
      </div>

      {tab === 'discussion' && <DiscussionTab messages={messages} msgDraft={msgDraft} setMsgDraft={setMsgDraft} postMessage={postMessage} />}

      {tab === 'meetings' && (
        <>
          <form className="card" onSubmit={createMeeting} style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Titre de réunion</label>
              <input required value={mTitle} onChange={(e) => setMTitle(e.target.value)} />
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" value={mDate} onChange={(e) => setMDate(e.target.value)} />
            </div>
            <div className="field">
              <label>Contenu / CR</label>
              <textarea
                rows={4}
                value={mContent}
                onChange={(e) => setMContent(e.target.value)}
                placeholder="CR de réunion — tu peux ensuite demander à l'assistant IA de le reformater en charte WSP."
              />
            </div>
            <div className="field">
              <label>Décisions (facultatif)</label>
              <textarea rows={2} value={mDecisions} onChange={(e) => setMDecisions(e.target.value)} />
            </div>
            <button className="btn btn-primary">Enregistrer la réunion</button>
          </form>

          {meetings.length === 0 && <div className="card card-meta">Aucune réunion.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {meetings.map((m) => (
              <div key={m.id} className="card">
                <div className="card-meta">
                  {m.meeting_date ?? new Date(m.created_at).toLocaleDateString('fr-FR')} ·{' '}
                  {m.created_by_name}
                </div>
                <div className="card-title">{m.title}</div>
                {m.content && <p style={{ whiteSpace: 'pre-wrap' }}>{m.content}</p>}
                {m.decisions && (
                  <>
                    <div className="card-meta">Décisions</div>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{m.decisions}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'decisions' && (
        <>
          <form className="card" onSubmit={createDecision} style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Décision</label>
              <input required value={dSummary} onChange={(e) => setDSummary(e.target.value)} />
            </div>
            <div className="field">
              <label>Contexte</label>
              <textarea rows={2} value={dContext} onChange={(e) => setDContext(e.target.value)} />
            </div>
            <button className="btn btn-primary">Acter la décision</button>
          </form>
          {decisions.length === 0 && <div className="card card-meta">Aucune décision tracée.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {decisions.map((d) => (
              <div key={d.id} className="card">
                <div className="card-meta">
                  {new Date(d.decided_at).toLocaleString('fr-FR')} · {d.validated_by_name}
                </div>
                <div style={{ fontWeight: 600 }}>{d.summary}</div>
                {d.context && <div className="card-meta" style={{ marginTop: 4 }}>{d.context}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
